import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { FaArrowLeft, FaChevronRight } from "react-icons/fa";
import {
  LdsTable,
  LdsTableBody,
  LdsTableCard,
  LdsTableCell,
  LdsTableHead,
  LdsTableHeadCell,
  LdsTableRow,
  LdsTableScroll,
} from "../../../components/lds/LdsTable";
import { runReport } from "../api";
import { drillDefinition, resolvePath } from "../definition";
import { formatSummaryCell, summaryHeader } from "../labels";
import { useReportRun } from "../useReportRun";
import type { Catalog, ReportResult, ReportSpec } from "../types";
import ResultTable from "./ResultTable";
import { fitWidth, ghostButton, secondaryButton } from "./styles";

type Props = {
  catalog: Catalog;
  definition: ReportSpec;
  result: ReportResult;
  stale?: boolean;
  loading?: boolean;
  onPage: (page: number) => void;
};

const DETAIL_PAGE_SIZE = 50;

/** A summary: one row per group; a group can be opened to see the rows behind it (drill-down). */
export default function SummaryResult({ catalog, definition, result, stale, loading, onPage }: Props): ReactElement {
  const { t } = useTranslation();
  const detail = useReportRun(t);
  const [drill, setDrill] = useState<{ label: string; definition: ReportSpec } | null>(null);

  const columns = result.columns.map((column) => ({
    column,
    header: summaryHeader(t, catalog, definition, column),
    field: column.display === "GROUP" && column.path
      ? resolvePath(catalog, definition.source, column.path)?.field ?? null
      : null,
  }));
  const groupColumns = columns.filter(({ column }) => column.display === "GROUP");

  const open = (row: Record<string, unknown>) => {
    const target = drillDefinition(catalog, definition, row);
    if (!target) return;
    const label = groupColumns.map(({ column, field }) => formatSummaryCell(t, column, field, row[column.key])).join(" · ");
    setDrill({ label, definition: target });
    detail.run((page, signal) => runReport(target, page, DETAIL_PAGE_SIZE, signal), 0);
  };

  if (drill) {
    return (
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <button type="button" className={secondaryButton} onClick={() => { setDrill(null); detail.reset(); }}>
            <FaArrowLeft aria-hidden /> {t("reports.builder.back_to_summary", { defaultValue: "Back to the summary" })}
          </button>
          <span className="font-bold text-slate-700 dark:text-slate-200">{drill.label}</span>
        </div>
        {detail.error && <p role="alert" className="text-sm text-rose-700 dark:text-rose-200">{detail.error}</p>}
        {detail.result && (
          <ResultTable
            catalog={catalog}
            definition={drill.definition}
            result={detail.result}
            loading={detail.loading}
            onPage={(page) => detail.run((p, signal) => runReport(drill.definition, p, DETAIL_PAGE_SIZE, signal), page)}
          />
        )}
      </div>
    );
  }

  const first = result.total === 0 ? 0 : result.page * result.size + 1;
  const last = Math.min(result.total, (result.page + 1) * result.size);
  const lastPage = Math.max(0, Math.ceil(result.total / result.size) - 1);
  const canDrill = drillDefinition(catalog, definition, {}) !== null;

  return (
    <div className="space-y-3">
      <LdsTableCard className={`${fitWidth}${stale ? " opacity-50" : ""}`} aria-busy={loading}>
        {stale && (
          <p className="px-5 pt-4 text-sm font-bold text-slate-600 dark:text-slate-300">
            {t("reports.builder.stale", { defaultValue: "The layout has changed – run the report again." })}
          </p>
        )}
        <LdsTableScroll className="p-3">
          <LdsTable>
            <LdsTableHead>
              <LdsTableRow>
                {columns.map(({ column, header }) => (
                  <LdsTableHeadCell key={column.key} className={column.display === "MEASURE" ? "text-right" : undefined}>
                    {header}
                  </LdsTableHeadCell>
                ))}
                {canDrill && <LdsTableHeadCell><span className="sr-only">{t("reports.builder.open", { defaultValue: "Open" })}</span></LdsTableHeadCell>}
              </LdsTableRow>
            </LdsTableHead>
            <LdsTableBody>
              {result.rows.length === 0 ? (
                <LdsTableRow>
                  <LdsTableCell colSpan={columns.length + 1} className="py-8 text-center text-slate-500">
                    {t("reports.builder.no_results", { defaultValue: "Nothing meets the conditions." })}
                  </LdsTableCell>
                </LdsTableRow>
              ) : result.rows.map((row, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <LdsTableRow key={index}>
                  {columns.map(({ column, field }) => (
                    <LdsTableCell key={column.key}
                                  className={column.display === "MEASURE" ? "text-right font-bold tabular-nums" : undefined}>
                      {formatSummaryCell(t, column, field, row[column.key])}
                    </LdsTableCell>
                  ))}
                  {canDrill && (
                    <LdsTableCell className="text-right">
                      <button type="button" className={ghostButton} disabled={stale} onClick={() => open(row)}>
                        {t("reports.builder.show_rows", { defaultValue: "Show rows" })} <FaChevronRight aria-hidden />
                      </button>
                    </LdsTableCell>
                  )}
                </LdsTableRow>
              ))}
            </LdsTableBody>
          </LdsTable>
        </LdsTableScroll>
      </LdsTableCard>
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-slate-600 dark:text-slate-300">
        <span>
          {t("reports.builder.groups_range", { first, last, total: result.total, defaultValue: "Groups {{first}}–{{last}} of {{total}}" })}
        </span>
        <div className="flex gap-2">
          <button type="button" className={secondaryButton} disabled={loading || result.page === 0} onClick={() => onPage(result.page - 1)}>
            {t("reports.builder.previous", { defaultValue: "Previous" })}
          </button>
          <button type="button" className={secondaryButton} disabled={loading || result.page >= lastPage} onClick={() => onPage(result.page + 1)}>
            {t("reports.builder.next", { defaultValue: "Next" })}
          </button>
        </div>
      </div>
    </div>
  );
}
